import { CONTROL_TOWER_DAILY_METRICS, DEPOT_PROFILES } from './mockData';
import { DailyDepotMetric, DepotDailySnapshot, DepotId, DepotProfile, MetricSnapshot } from './types';

const VEHICLE_DAY_COST_RS = 2000;

const safeDivide = (numerator: number, denominator: number) => (denominator === 0 ? 0 : numerator / denominator);

const roundTo = (value: number, decimals = 1) => {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
};

const formatWithGrouping = (value: number, maximumFractionDigits = 0) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits }).format(value);

export const depotById = DEPOT_PROFILES.reduce<Record<DepotId, DepotProfile>>((acc, depot) => {
  acc[depot.id] = depot;
  return acc;
}, {} as Record<DepotId, DepotProfile>);

export const toDepotDailySnapshot = (record: DailyDepotMetric): DepotDailySnapshot => {
  const totalAvailableTonnageKg = record.vehiclesAvailable * record.avgVehicleCapacityKg;
  const usedVehicleCapacityKg = record.vehiclesUsed * record.avgVehicleCapacityKg;
  const totalVehicleCost = record.vehiclesUsed * VEHICLE_DAY_COST_RS;

  return {
    depotId: record.depotId,
    date: record.date,
    touchPoints: record.touchPoints,
    tonnageKg: record.tonnageKg,
    volume: record.volume,
    awtKg: roundTo(safeDivide(record.tonnageKg, record.touchPoints), 1),
    vehiclesAvailable: record.vehiclesAvailable,
    avgVehicleCapacityKg: record.avgVehicleCapacityKg,
    totalAvailableTonnageKg,
    usedVehicleCapacityKg,
    totalTrips: record.totalTrips,
    vehiclesUsed: record.vehiclesUsed,
    touchPointsPerTrip: roundTo(safeDivide(record.touchPoints, record.totalTrips), 1),
    kgsPerTrip: roundTo(safeDivide(record.tonnageKg, record.totalTrips), 0),
    timePerTripHours: record.timePerTripHours,
    vehicleCapacityUtilizationPct: roundTo(safeDivide(record.tonnageKg, usedVehicleCapacityKg) * 100, 1),
    slotBreaches: record.slotBreaches,
    totalVehicleCost,
    costPerKg: roundTo(safeDivide(totalVehicleCost, record.tonnageKg), 2),
    costPerTouchPoint: roundTo(safeDivide(totalVehicleCost, record.touchPoints), 1),
  };
};

export const aggregateSnapshots = (date: string, snapshots: MetricSnapshot[]): MetricSnapshot => {
  const touchPoints = snapshots.reduce((sum, snapshot) => sum + snapshot.touchPoints, 0);
  const tonnageKg = snapshots.reduce((sum, snapshot) => sum + snapshot.tonnageKg, 0);
  const volume = snapshots.reduce((sum, snapshot) => sum + snapshot.volume, 0);
  const vehiclesAvailable = snapshots.reduce((sum, snapshot) => sum + snapshot.vehiclesAvailable, 0);
  const totalAvailableTonnageKg = snapshots.reduce((sum, snapshot) => sum + snapshot.totalAvailableTonnageKg, 0);
  const usedVehicleCapacityKg = snapshots.reduce((sum, snapshot) => sum + snapshot.usedVehicleCapacityKg, 0);
  const totalTrips = snapshots.reduce((sum, snapshot) => sum + snapshot.totalTrips, 0);
  const vehiclesUsed = snapshots.reduce((sum, snapshot) => sum + snapshot.vehiclesUsed, 0);
  const totalVehicleCost = snapshots.reduce((sum, snapshot) => sum + snapshot.totalVehicleCost, 0);
  const slotBreaches = snapshots.reduce((sum, snapshot) => sum + snapshot.slotBreaches, 0);
  const tripTimeWeight = snapshots.reduce((sum, snapshot) => sum + snapshot.timePerTripHours * snapshot.totalTrips, 0);

  return {
    date,
    touchPoints,
    tonnageKg,
    volume,
    awtKg: roundTo(safeDivide(tonnageKg, touchPoints), 1),
    vehiclesAvailable,
    avgVehicleCapacityKg: roundTo(safeDivide(totalAvailableTonnageKg, vehiclesAvailable), 0),
    totalAvailableTonnageKg,
    usedVehicleCapacityKg,
    totalTrips,
    vehiclesUsed,
    touchPointsPerTrip: roundTo(safeDivide(touchPoints, totalTrips), 1),
    kgsPerTrip: roundTo(safeDivide(tonnageKg, totalTrips), 0),
    timePerTripHours: roundTo(safeDivide(tripTimeWeight, totalTrips), 1),
    vehicleCapacityUtilizationPct: roundTo(safeDivide(tonnageKg, usedVehicleCapacityKg) * 100, 1),
    slotBreaches,
    totalVehicleCost,
    costPerKg: roundTo(safeDivide(totalVehicleCost, tonnageKg), 2),
    costPerTouchPoint: roundTo(safeDivide(totalVehicleCost, touchPoints), 1),
  };
};

export const getAggregateSeries = (
  depotIds: DepotId[],
  records: DailyDepotMetric[] = CONTROL_TOWER_DAILY_METRICS
): MetricSnapshot[] => {
  const selectedDepotIds = new Set(depotIds);
  const snapshotsByDate = records
    .filter((record) => selectedDepotIds.has(record.depotId))
    .reduce<Record<string, MetricSnapshot[]>>((acc, record) => {
      if (!acc[record.date]) {
        acc[record.date] = [];
      }
      acc[record.date].push(toDepotDailySnapshot(record));
      return acc;
    }, {});

  return Object.keys(snapshotsByDate)
    .sort()
    .map((date) => aggregateSnapshots(date, snapshotsByDate[date]));
};

export const getDepotSeries = (
  depotId: DepotId,
  records: DailyDepotMetric[] = CONTROL_TOWER_DAILY_METRICS
): DepotDailySnapshot[] =>
  records
    .filter((record) => record.depotId === depotId)
    .sort((left, right) => left.date.localeCompare(right.date))
    .map(toDepotDailySnapshot);

export const getLatestSnapshot = <T extends MetricSnapshot>(series: T[]): T => series[series.length - 1];

export const getPreviousSnapshot = <T extends MetricSnapshot>(series: T[]): T | null =>
  series.length > 1 ? series[series.length - 2] : null;

export const getPercentDelta = (current: number, previous: number | null) => {
  if (!previous) {
    return 0;
  }
  return roundTo(((current - previous) / previous) * 100, 1);
};

export const formatShortDate = (date: string) =>
  new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(`${date}T00:00:00`));

export const formatNumber = (value: number, maximumFractionDigits = 0) => formatWithGrouping(value, maximumFractionDigits);

export const formatTonnes = (kg: number) => `${formatWithGrouping(kg / 1000, 1)}T`;

export const formatKg = (kg: number) => `${formatWithGrouping(kg, 0)} kg`;

export const formatHours = (hours: number) => `${formatWithGrouping(hours, 1)} hrs`;

export const formatPercent = (value: number) => `${formatWithGrouping(value, 1)}%`;

export const formatCurrency = (value: number) => `Rs ${formatWithGrouping(value, 0)}`;

export const formatCurrencyPer = (value: number, suffix: string) => `Rs ${formatWithGrouping(value, 2)}/${suffix}`;
